'use client'

import { m } from 'framer-motion'
import {
  Calendar,
  ChevronRight,
  Clock,
  Copy,
  Link as LinkIcon,
  Sparkles,
  Tag,
  Twitter,
} from 'lucide-react'
import Image from 'next/image'
import { useEffect, useMemo, useRef, useState } from 'react'

import { formatNewsCategoryLabel } from '@/shared/constants/directions'

export type ArticleUiSettings = {
  showCoverCard?: boolean
  showShareCard?: boolean
  showCategoryBadge?: boolean
  showReadTime?: boolean
}

interface ArticleContentProps {
  slug: string
  content: string
  excerpt: string
  coverImage?: string | null
  category?: string | null
  publishedAt?: Date | string
  settings?: ArticleUiSettings | null
}

function estimateReadTimeMinutes(text: string) {
  const words = text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean).length
  return Math.max(3, Math.round(words / 200))
}

function formatRuDateUTC(d: Date | string | undefined | null) {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeArticleHtml(raw: string) {
  const source = (raw || '').trim()
  if (!source) return ''

  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(source)
  if (hasHtml) return source

  return source
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => `<p>${escapeHtml(chunk).replace(/\n/g, '<br />')}</p>`)
    .join('')
}

function stripTags(input: string) {
  return input
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

type HeadingPoint = {
  id: string
  text: string
  level: 2 | 3
}

function slugifyHeading(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\u0400-\u04ff\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

function enhanceHtmlWithHeadingIds(html: string) {
  const used = new Map<string, number>()
  const headings: HeadingPoint[] = []

  const output = html.replace(
    /<h([23])([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (full, levelRaw: string, attrs: string, inner: string) => {
      const level = Number(levelRaw) as 2 | 3
      const text = stripTags(inner)
      if (!text) return full

      const idMatch = attrs?.match(/\sid\s*=\s*["']([^"']+)["']/i)
      let id = idMatch?.[1]?.trim() || ''

      if (!id) {
        const base = slugifyHeading(text) || `section-${headings.length + 1}`
        const count = used.get(base) ?? 0
        used.set(base, count + 1)
        id = count > 0 ? `${base}-${count + 1}` : base
      }

      headings.push({ id, text, level })

      if (idMatch) return full
      return `<h${level}${attrs} id="${id}">${inner}</h${level}>`
    },
  )

  if (headings.length > 0) return { html: output, headings }

  const paragraphPoints: HeadingPoint[] = []
  let pointIndex = 0
  const htmlWithParagraphAnchors = output.replace(
    /<p([^>]*)>([\s\S]*?)<\/p>/gi,
    (full, attrs: string, inner: string) => {
      if (paragraphPoints.length >= 6) return full

      const text = stripTags(inner)
      if (!text || text.length < 24) return full

      const idMatch = attrs?.match(/\sid\s*=\s*["']([^"']+)["']/i)
      const id = idMatch?.[1]?.trim() || `point-${++pointIndex}`
      paragraphPoints.push({
        id,
        text: text.length > 90 ? `${text.slice(0, 87).trim()}...` : text,
        level: 3,
      })

      if (idMatch) return full
      return `<p${attrs} id="${id}">${inner}</p>`
    },
  )

  return { html: htmlWithParagraphAnchors, headings: paragraphPoints }
}

export default function ArticleContent({
  slug,
  content,
  excerpt,
  coverImage,
  category,
  publishedAt,
  settings,
}: ArticleContentProps) {
  const [copied, setCopied] = useState(false)
  const [coverBroken, setCoverBroken] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const articleWrapRef = useRef<HTMLDivElement | null>(null)
  const sidebarColumnRef = useRef<HTMLElement | null>(null)
  const sidebarInnerRef = useRef<HTMLDivElement | null>(null)
  const [sidebarDock, setSidebarDock] = useState({
    mode: 'static' as 'static' | 'fixed' | 'bottom',
    left: 0,
    width: 0,
    height: 0,
    topOffset: 96,
    maxHeight: 0,
  })

  const showCoverCard = settings?.showCoverCard ?? true
  const showShareCard = settings?.showShareCard ?? true
  const showCategoryBadge = settings?.showCategoryBadge ?? true
  const showReadTime = settings?.showReadTime ?? true

  const readTimeMinutes = useMemo(() => {
    return estimateReadTimeMinutes(`${excerpt || ''} ${content || ''}`)
  }, [content, excerpt])

  const normalizedHtml = useMemo(() => normalizeArticleHtml(content), [content])
  const { html: articleHtml, headings } = useMemo(
    () => enhanceHtmlWithHeadingIds(normalizedHtml),
    [normalizedHtml],
  )

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return `${window.location.origin}/news/${slug}`
  }, [slug])

  const twitterShareUrl = useMemo(() => {
    if (!shareUrl) return '#'
    const text = `Цифровой горизонт — ${excerpt || ''}`.trim()
    const qs = new URLSearchParams({ url: shareUrl, text }).toString()
    return `https://twitter.com/intent/tweet?${qs}`
  }, [shareUrl, excerpt])

  async function handleCopy() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    const onScroll = () => {
      const root = articleWrapRef.current
      if (!root) return

      const rect = root.getBoundingClientRect()
      const viewport = window.innerHeight
      const total = rect.height + viewport
      const passed = viewport - rect.top
      const raw = total > 0 ? (passed / total) * 100 : 0
      const clamped = Math.max(0, Math.min(100, raw))
      setScrollProgress(clamped)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  useEffect(() => {
    let raf = 0

    const updateSidebarDock = () => {
      raf = 0
      const column = sidebarColumnRef.current
      const inner = sidebarInnerRef.current
      if (!column || !inner) return

      const desktopLike = window.innerWidth >= 768
      const topOffset = window.innerWidth >= 1024 ? 112 : 96

      if (!desktopLike) {
        setSidebarDock((prev) =>
          prev.mode !== 'static'
            ? { mode: 'static', left: 0, width: 0, height: 0, topOffset, maxHeight: 0 }
            : prev,
        )
        return
      }

      const columnRect = column.getBoundingClientRect()
      const contentHeight = inner.scrollHeight
      const maxHeight = Math.max(220, window.innerHeight - topOffset - 16)
      const fixedHeight = Math.min(contentHeight, maxHeight)

      if (columnRect.top > topOffset) {
        setSidebarDock((prev) =>
          prev.mode !== 'static'
            ? { mode: 'static', left: 0, width: 0, height: 0, topOffset, maxHeight: 0 }
            : prev,
        )
        return
      }

      if (columnRect.bottom <= topOffset + fixedHeight) {
        setSidebarDock((prev) => {
          const next = {
            mode: 'bottom' as const,
            left: 0,
            width: 0,
            height: contentHeight,
            topOffset,
            maxHeight,
          }
          const unchanged =
            prev.mode === next.mode &&
            Math.abs(prev.height - next.height) < 1 &&
            prev.topOffset === next.topOffset &&
            Math.abs(prev.maxHeight - next.maxHeight) < 1
          return unchanged ? prev : next
        })
        return
      }

      const next = {
        mode: 'fixed' as const,
        left: columnRect.left,
        width: columnRect.width,
        height: contentHeight,
        topOffset,
        maxHeight,
      }

      setSidebarDock((prev) => {
        const unchanged =
          prev.mode === next.mode &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs(prev.width - next.width) < 0.5 &&
          Math.abs(prev.height - next.height) < 1 &&
          prev.topOffset === next.topOffset &&
          Math.abs(prev.maxHeight - next.maxHeight) < 1

        return unchanged ? prev : next
      })
    }

    const onScroll = () => {
      if (raf) return
      raf = window.requestAnimationFrame(updateSidebarDock)
    }

    const onResize = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(updateSidebarDock)
    }

    updateSidebarDock()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const renderArticlePanel = () => (
    <>
      <div className="mb-8 h-1 w-20 rounded-full bg-gradient-to-r from-blue-400 via-cyan-300 to-purple-400 shadow-[0_0_22px_rgba(56,189,248,0.45)] sm:mb-10 sm:w-24" />

      <div
        ref={articleWrapRef}
        className="relative rounded-[2rem] bg-gradient-to-br from-white/20 via-white/8 to-blue-300/20 p-[1px] shadow-[0_24px_90px_rgba(2,6,23,0.55)] sm:rounded-[2.5rem]"
      >
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(160deg,rgba(8,15,31,0.94),rgba(4,9,22,0.97))] sm:rounded-[2.5rem]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgba(59,130,246,0.18),transparent_55%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_100%,rgba(168,85,247,0.14),transparent_50%)]" />
          <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/50 to-transparent" />

          <article className="relative z-10 px-6 py-8 sm:px-10 sm:py-12 md:px-14 md:py-14 lg:px-16 lg:py-16">
            <span id="article-top" className="block scroll-mt-28" />
            <div
              dangerouslySetInnerHTML={{ __html: articleHtml }}
              className="home-subnote leading-[1.82] text-gray-200/95 [&_blockquote]:my-12 [&_blockquote]:rounded-2xl [&_blockquote]:border [&_blockquote]:border-cyan-300/25 [&_blockquote]:bg-cyan-400/8 [&_blockquote]:px-5 [&_blockquote]:py-4 [&_blockquote]:text-[clamp(1.14rem,2.4vw,1.6rem)] [&_blockquote]:leading-relaxed [&_blockquote]:text-white sm:[&_blockquote]:px-7 sm:[&_blockquote]:py-6 [&_code]:text-[0.92em] [&_code]:text-cyan-200 [&_h2]:mt-14 [&_h2]:mb-6 [&_h2]:scroll-mt-28 [&_h2]:text-[clamp(1.8rem,3.8vw,3.2rem)] [&_h2]:leading-tight [&_h2]:font-semibold [&_h2]:text-white sm:[&_h2]:mt-18 sm:[&_h2]:mb-8 [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:scroll-mt-28 [&_h3]:text-[clamp(1.35rem,2.8vw,2rem)] [&_h3]:leading-tight [&_h3]:font-semibold [&_h3]:text-blue-100 sm:[&_h3]:mt-12 sm:[&_h3]:mb-6 [&_li]:relative [&_li]:mb-4 [&_li]:pl-6 [&_li]:text-gray-300 [&_li::before]:absolute [&_li::before]:top-[0.72em] [&_li::before]:left-0 [&_li::before]:h-2 [&_li::before]:w-2 [&_li::before]:rounded-full [&_li::before]:bg-cyan-300 [&_li::before]:content-[''] [&_p]:mb-6 [&_p]:text-[clamp(1.02rem,1.1vw,1.16rem)] [&_p]:text-gray-300 sm:[&_p]:mb-8 [&_p:first-of-type]:mb-10 [&_p:first-of-type]:text-[clamp(1.18rem,2.05vw,1.78rem)] [&_p:first-of-type]:leading-snug [&_p:first-of-type]:font-medium [&_p:first-of-type]:text-white sm:[&_p:first-of-type]:mb-12 [&_pre]:my-10 [&_pre]:overflow-x-auto [&_pre]:rounded-2xl [&_pre]:border [&_pre]:border-white/12 [&_pre]:bg-black/45 [&_pre]:p-4 sm:[&_pre]:p-6 [&_strong]:font-semibold [&_strong]:text-white [&_ul]:mb-10 [&_ul]:list-none [&_ul]:pl-0"
            />
          </article>
        </div>
      </div>
    </>
  )

  const renderCoverCard = (compact = false) => {
    if (!showCoverCard) return null

    return (
      <m.div
        initial={{ opacity: 0, x: 18 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.12 }}
        className="group relative rounded-[1.9rem] bg-gradient-to-br from-cyan-300/40 via-white/10 to-fuchsia-400/35 p-[1px] shadow-[0_20px_70px_rgba(2,6,23,0.5)]"
      >
        <div className="relative overflow-hidden rounded-[1.9rem] border border-white/10 bg-[#070d1a]/92">
          <div
            className={`relative ${compact ? 'aspect-[16/9]' : 'aspect-[16/10] lg:aspect-[4/5]'} w-full overflow-hidden`}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(59,130,246,0.35),transparent_45%),radial-gradient(circle_at_80%_80%,rgba(168,85,247,0.32),transparent_45%),linear-gradient(160deg,#070d1a,#0b1530)]" />
            {!coverBroken && coverImage ? (
              <Image
                src={coverImage}
                alt="Обложка статьи"
                fill
                priority
                sizes={
                  compact
                    ? '(max-width: 768px) 100vw, (max-width: 1280px) 67vw, 900px'
                    : '(max-width: 768px) 100vw, (max-width: 1280px) 30vw, 420px'
                }
                className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                onLoad={() => setCoverBroken(false)}
                onError={() => setCoverBroken(true)}
              />
            ) : null}

            <div className="absolute inset-0 bg-gradient-to-t from-[#040813] via-[#040813]/35 to-transparent" />

            {showCategoryBadge && category ? (
              <div className="absolute top-4 left-4">
                <span className="home-meta inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/35 px-3 py-1.5 text-white/95 backdrop-blur-md">
                  <Tag size={12} className="text-cyan-300" />
                  {formatNewsCategoryLabel(category)}
                </span>
              </div>
            ) : null}
          </div>

          <div className="border-t border-white/10 bg-[linear-gradient(150deg,rgba(11,19,37,0.9),rgba(9,15,30,0.95))] p-5 sm:p-6">
            <div className="home-meta mb-4 flex items-center justify-between gap-3 text-gray-500">
              <span className="inline-flex items-center gap-1.5 truncate">
                <Calendar size={12} className="shrink-0 text-blue-300" />
                {formatRuDateUTC(publishedAt)}
              </span>
              {showReadTime ? (
                <span className="inline-flex shrink-0 items-center gap-1.5">
                  <Clock size={12} className="text-purple-300" />
                  {readTimeMinutes} мин
                </span>
              ) : null}
            </div>

            <p className="home-subnote line-clamp-4 leading-relaxed text-gray-200/90">{excerpt}</p>
          </div>
        </div>
      </m.div>
    )
  }

  const renderShareCard = () => {
    if (!showShareCard) return null

    return (
      <m.div
        initial={{ opacity: 0, x: 18 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="relative rounded-[1.8rem] bg-gradient-to-br from-white/20 via-cyan-300/20 to-purple-400/25 p-[1px]"
      >
        <div className="rounded-[1.8rem] border border-white/10 bg-[linear-gradient(150deg,rgba(10,17,33,0.95),rgba(7,13,26,0.96))] p-5 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div className="home-meta tracking-[0.2em] text-gray-400 uppercase">Поделиться</div>
            {copied ? <div className="home-meta text-emerald-300">Скопировано</div> : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a
              href={twitterShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="home-subnote inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] py-3 text-white transition-all hover:border-cyan-300/35 hover:bg-white/[0.1]"
            >
              <Twitter size={16} className="text-blue-300" />X
            </a>

            <button
              type="button"
              onClick={handleCopy}
              className="home-subnote inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] py-3 text-white transition-all hover:border-cyan-300/35 hover:bg-white/[0.1]"
            >
              {copied ? (
                <LinkIcon size={16} className="text-emerald-300" />
              ) : (
                <Copy size={16} className="text-purple-300" />
              )}
              Ссылка
            </button>
          </div>
        </div>
      </m.div>
    )
  }

  const renderMetaStrip = () => (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#0b1429]/70 px-5 py-4 backdrop-blur-xl sm:gap-6 sm:px-6 sm:py-4">
      <span className="home-meta inline-flex items-center gap-2 text-gray-300">
        <Sparkles size={12} className="text-cyan-300" />
        Отдельная публикация
      </span>
      <span className="home-meta inline-flex items-center gap-2 text-gray-400">
        <Calendar size={12} className="text-blue-300" />
        {formatRuDateUTC(publishedAt)}
      </span>
      {showReadTime ? (
        <span className="home-meta inline-flex items-center gap-2 text-gray-400">
          <Clock size={12} className="text-purple-300" />
          {readTimeMinutes} мин
        </span>
      ) : null}
      {showCategoryBadge && category ? (
        <span className="home-meta ml-auto inline-flex items-center gap-2 text-white/90">
          <Tag size={12} className="text-cyan-300" />
          {category}
        </span>
      ) : null}
    </div>
  )

  const renderOutlineCard = () => (
    <div className="rounded-[1.7rem] border border-cyan-300/25 bg-[linear-gradient(160deg,rgba(12,24,46,0.88),rgba(10,19,37,0.9))] p-5 shadow-[0_20px_50px_rgba(8,47,73,0.28)] sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles size={14} className="text-cyan-300" />
        <p className="home-meta tracking-[0.18em] text-cyan-200 uppercase">Кратко по статье</p>
      </div>

      <ul className="space-y-3">
        {(headings.length > 0
          ? headings.slice(0, 6)
          : [{ id: 'article-top', text: 'Начало статьи', level: 2 as const }]
        ).map((item, idx) => (
          <li key={`${item.id}-${idx}`} className="items-start">
            <a
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault()
                const target = document.getElementById(item.id)
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }}
              className="group flex items-start gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-cyan-300/8"
            >
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
              <span className="home-subnote leading-relaxed text-gray-200 transition-colors group-hover:text-white">
                {item.text}
              </span>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 -translate-x-1 text-cyan-300/70 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  )

  const renderProgressCard = () => (
    <div className="rounded-[1.7rem] border border-white/10 bg-[#091327]/85 p-5 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="home-meta tracking-[0.18em] text-gray-400 uppercase">Прогресс чтения</p>
        <span className="home-meta text-cyan-200">{Math.round(scrollProgress)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-300 to-purple-400 transition-[width] duration-200"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
    </div>
  )

  const renderBriefFactsCard = () => (
    <div className="rounded-[1.7rem] border border-white/10 bg-[#091327]/85 p-5 sm:p-6">
      <p className="home-meta mb-4 tracking-[0.18em] text-gray-400 uppercase">Паспорт материала</p>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="home-meta text-gray-500">Дата публикации</span>
          <span className="home-subnote text-white">{formatRuDateUTC(publishedAt)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="home-meta text-gray-500">Время чтения</span>
          <span className="home-subnote text-white">{readTimeMinutes} мин</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="home-meta text-gray-500">Формат</span>
          <span className="home-subnote text-white">Аналитика + практикум</span>
        </div>
        {category ? (
          <div className="flex items-center justify-between gap-3">
            <span className="home-meta text-gray-500">Категория</span>
            <span className="home-subnote text-white">{category}</span>
          </div>
        ) : null}
      </div>
    </div>
  )

  return (
    <section className="relative z-10 pb-28 sm:pb-36">
      <div className="home-container relative z-10 px-0 sm:px-2 md:px-6 xl:px-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-9 lg:gap-10 xl:gap-12">
          <aside
            ref={sidebarColumnRef}
            className="relative order-1 hidden min-w-0 md:col-span-4 md:block xl:col-span-3"
            style={
              sidebarDock.mode !== 'static' ? { minHeight: `${sidebarDock.height}px` } : undefined
            }
          >
            <div
              ref={sidebarInnerRef}
              className={[
                'space-y-5 sm:space-y-6',
                sidebarDock.mode === 'fixed' ? 'no-scrollbar overflow-y-auto pr-1' : '',
              ].join(' ')}
              style={
                sidebarDock.mode === 'fixed'
                  ? {
                      position: 'fixed',
                      top: `${sidebarDock.topOffset}px`,
                      left: `${sidebarDock.left}px`,
                      width: `${sidebarDock.width}px`,
                      maxHeight: `${sidebarDock.maxHeight}px`,
                    }
                  : sidebarDock.mode === 'bottom'
                    ? {
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                      }
                    : undefined
              }
            >
              {renderProgressCard()}
              {renderOutlineCard()}
              {renderBriefFactsCard()}
              {renderShareCard()}
            </div>
          </aside>

          <div className="order-1 min-w-0 space-y-6 sm:space-y-8 md:order-2 md:col-span-8 xl:col-span-9">
            <div className="hidden md:block">{renderMetaStrip()}</div>
            <div className="md:hidden">{renderOutlineCard()}</div>
            {renderCoverCard(true)}
            {renderArticlePanel()}
            <div className="space-y-5 md:hidden">
              {renderShareCard()}
              {renderMetaStrip()}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
